import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import apiService from "../services/apiService";

const EmailOtpModal = ({ clientId, loanId, clientName, clientEmail, onClose, onVerified }) => {
  const [step, setStep] = useState("confirm"); // confirm | input | success
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [verificationId, setVerificationId] = useState(null);
  const [maskedEmail, setMaskedEmail] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const [expiryCountdown, setExpiryCountdown] = useState(0);
  const inputRefs = useRef([]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setInterval(() => {
      setResendCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  // Expiry countdown timer
  useEffect(() => {
    if (expiryCountdown <= 0) return;
    const timer = setInterval(() => {
      setExpiryCountdown((prev) => {
        if (prev <= 1) {
          toast.error("Verification code has expired. Please resend.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [expiryCountdown]);

  const maskEmail = (email) => {
    if (!email || !email.includes("@")) return email;
    const [local, domain] = email.split("@");
    return local.charAt(0) + "***" + local.charAt(local.length - 1) + "@" + domain;
  };

  const sendOtp = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.post(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/send-email-otp`,
        { clientId, loanId }
      );
      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || "Failed to send verification code");
        return;
      }

      setVerificationId(data.verificationId);
      setMaskedEmail(data.email);
      setStep("input");
      setOtp(["", "", "", "", "", ""]);
      setResendCountdown(60);
      setExpiryCountdown(600); // 10 minutes
      toast.success("Verification code sent!");

      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch (err) {
      console.error("Send OTP error:", err);
      toast.error("Failed to send verification code");
    } finally {
      setLoading(false);
    }
  }, [clientId, loanId]);

  const verifyOtp = async () => {
    const code = otp.join("");
    if (code.length !== 6) {
      toast.error("Please enter the complete 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.post(
        `${process.env.REACT_APP_API_HOST_COLLECTIONS}/verify-email-otp`,
        { clientId, otpCode: code }
      );
      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || "Verification failed");
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
        return;
      }

      setStep("success");
      toast.success("Email verified successfully!");
      if (onVerified) onVerified();
    } catch (err) {
      console.error("Verify OTP error:", err);
      toast.error("Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const newOtp = [...otp];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = pasted[i] || "";
    }
    setOtp(newOtp);
    const nextEmpty = newOtp.findIndex((d) => !d);
    const focusIdx = nextEmpty === -1 ? 5 : nextEmpty;
    inputRefs.current[focusIdx]?.focus();
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">
            {step === "success" ? "Verification Complete" : "Email Verification"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Confirm Step */}
          {step === "confirm" && (
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-gray-700 mb-2">
                A verification code will be sent to:
              </p>
              <p className="text-lg font-semibold text-gray-900 mb-1">
                {clientName || "Client"}
              </p>
              <p className="text-blue-600 font-medium mb-6">
                {maskEmail(clientEmail)}
              </p>
              <button
                onClick={sendOtp}
                disabled={loading}
                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold rounded-lg transition-colors"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Sending...
                  </span>
                ) : (
                  "Send Verification Code"
                )}
              </button>
            </div>
          )}

          {/* Input Step */}
          {step === "input" && (
            <div className="text-center">
              <p className="text-gray-600 mb-1">
                Enter the 6-digit code sent to
              </p>
              <p className="text-blue-600 font-medium mb-6">{maskedEmail}</p>

              {/* OTP Inputs */}
              <div className="flex justify-center gap-2 mb-4" onPaste={handlePaste}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
                  />
                ))}
              </div>

              {/* Expiry countdown */}
              {expiryCountdown > 0 && (
                <p className="text-sm text-gray-500 mb-4">
                  Code expires in <span className="font-semibold text-gray-700">{formatTime(expiryCountdown)}</span>
                </p>
              )}

              {/* Actions */}
              <div className="space-y-3">
                <button
                  onClick={verifyOtp}
                  disabled={loading || otp.join("").length !== 6}
                  className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white font-semibold rounded-lg transition-colors"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Verifying...
                    </span>
                  ) : (
                    "Verify"
                  )}
                </button>
                <button
                  onClick={sendOtp}
                  disabled={loading || resendCountdown > 0}
                  className="w-full py-2 px-4 text-blue-600 hover:text-blue-800 disabled:text-gray-400 font-medium transition-colors text-sm"
                >
                  {resendCountdown > 0
                    ? `Resend code in ${resendCountdown}s`
                    : "Resend Code"}
                </button>
              </div>
            </div>
          )}

          {/* Success Step */}
          {step === "success" && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Email Verified</h3>
              <p className="text-gray-600 mb-6">
                {clientName ? `${clientName}'s` : "The client's"} email has been successfully verified.
              </p>
              <button
                onClick={onClose}
                className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailOtpModal;
